/// <reference path="../../react-datepicker.d.ts" />

import * as React from 'react';

const ReactDatePicker = require('react-datepicker');
const moment = require('moment');

import { IError, IInputChangeHandler } from '../../types';

import FieldFeedbackPanel from './FieldFeedbackPanel';

export default ({object, error, name, label, onChange}: { object: any, error: IError, name: string, label: string, onChange: IInputChangeHandler }) => {
  // CRITICAL: Wrap entire function in try-catch as final safety net
  try {
    // CRITICAL: Store object in a const IMMEDIATELY after validation
    // This prevents the object from changing during the function execution
    // Check every possible way object could be invalid
    // IMPORTANT: Check for null/undefined FIRST before accessing any properties
    // Use == null to catch both null and undefined in one check
    // This MUST be the first check - do not access object properties before this
    if (object == null || 
        object === undefined || 
        object === null) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('DateInput: object is null/undefined - early return', { object, name, label });
      }
      return null;
    }
    
    // Now safe to check type and array
    if (typeof object !== 'object' || Array.isArray(object)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('DateInput: object is not a valid object - early return', { object, name, label, objectType: typeof object, isArray: Array.isArray(object) });
      }
      return null;
    }
    
    // CRITICAL: Store the validated object in a const immediately
    // This ensures the object reference doesn't change during execution
    const validatedObject = object;
    
    // Re-validate the stored object (defensive programming)
    if (validatedObject == null || 
        validatedObject === undefined || 
        validatedObject === null || 
        typeof validatedObject !== 'object' || 
        Array.isArray(validatedObject)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('DateInput: validatedObject failed re-validation', { validatedObject, name, label });
      }
      return null;
    }

    // Ensure onChange is a function
    if (typeof onChange !== 'function') {
      return null;
    }

    const handleOnChange = value => {
      // Format date as YYYY-MM-DD (ISO format) for backend compatibility
      const dateString = value ? value.format('YYYY-MM-DD') : null;
      if (onChange && typeof onChange === 'function') {
        onChange(name, dateString, null);
      }
    };

    // Get value with maximum safety - use validatedObject (stored const)
    let value = null;
    
    // CRITICAL: Use validatedObject (the const we stored) instead of object parameter
    // This ensures we're always working with the validated reference
    // Add one more validation check RIGHT before property access
    if (!validatedObject || validatedObject === null || validatedObject === undefined || typeof validatedObject !== 'object' || Array.isArray(validatedObject)) {
      console.warn('DateInput: validatedObject is invalid before property access', { validatedObject, name, label });
      return null;
    }
    
    try {
      // Final validation of validatedObject (defensive programming)
      // Store in a local const to prevent any changes
      const safeObject = validatedObject;
      if (!safeObject || safeObject === null || safeObject === undefined || typeof safeObject !== 'object' || Array.isArray(safeObject)) {
        return null;
      }
      
      // Use hasOwnProperty which is safer than 'in' operator
      // Check if property exists before accessing
      if (Object.prototype.hasOwnProperty.call(safeObject, name)) {
        // Access the property - safeObject is guaranteed to be valid
        const propertyValue = safeObject[name];
        if (propertyValue !== undefined && propertyValue !== null) {
          value = propertyValue;
        }
      }
      // If property doesn't exist, value stays null (which is fine)
    } catch (e) {
      // If anything goes wrong (shouldn't happen, but be safe), value stays null
      console.error('DateInput: Error accessing property', name, 'on validatedObject:', e, 'validatedObject:', validatedObject);
      value = null;
    }
    // Parse date value - try multiple formats that might come from backend
    let selectedValue = null;
    if (value) {
      try {
        // Try parsing with different formats
        const parsed = moment(value, ['YYYY-MM-DD', 'YYYY/MM/DD', 'MM/DD/YYYY', 'DD/MM/YYYY'], true);
        if (parsed.isValid()) {
          selectedValue = parsed;
        } else {
          // If strict parsing fails, try loose parsing
          selectedValue = moment(value);
          if (!selectedValue.isValid()) {
            selectedValue = null;
          }
        }
      } catch (e) {
        console.error('Error parsing date:', e, 'Value:', value);
        selectedValue = null;
      }
    }
    const fieldError = error && error.fieldErrors ? error.fieldErrors[name] : undefined;
    const valid = !fieldError && selectedValue != null;

    const cssGroup = `form-group ${fieldError ? 'has-error' : ''}`;

    return (
      <div className={cssGroup}>
        <label className='col-sm-2 control-label'>{label}</label>

        <div className='col-sm-10'>
          <ReactDatePicker selected={selectedValue} onChange={handleOnChange} className='form-control' dateFormat='YYYY-MM-DD' />
          <span className='glyphicon glyphicon-ok form-control-feedback' aria-hidden='true'></span>
          <FieldFeedbackPanel valid={valid} fieldError={fieldError} />
        </div>
      </div>
    );
  } catch (e) {
    // Final safety net - if anything goes wrong, return null instead of crashing
    console.error('DateInput: Unexpected error in render', e, { object, name, label });
    return null;
  }
};
